import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
// Iconos: usar set SVG oficial de Vuetify (mdi-svg)
// Usar el set envuelto que normaliza cualquier string 'mdi-*' a path SVG
import { aliases, mdi } from '@/plugins/mdiSetCompat'
import router from './router'
import App from './App.vue'

// Vuetify
import 'vuetify/styles'
import '@/assets/styles/dialogs.css'
import '@/styles/neumorphism.css'
import { md3 } from 'vuetify/blueprints'

// Manejador global para promesas no capturadas - MEJORADO
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason
  
  // Solo manejar errores HTTP específicos para evitar ruido en consola
  if (
    error?.message?.includes('HTTP 404') ||
    error?.message?.includes('HTTP 400') ||
    error?.message?.includes('HTTP 409') ||
    error?.isHandled === true
  ) {
    // Evitar logs duplicados - solo prevenir el error, no loggear
    event.preventDefault() // Evita que aparezca como error no capturado
    return // No hacer console.log aquí para evitar duplicados
  }
  
  // Para otros errores, permitir que se muestren normalmente
})

// Formularios Inteligentes - Optimización
import { FormOptimizationPlugin, vLazyForm } from '@/plugins/formOptimization'
import FormComponents from '@/components/forms'

// 📊 Analytics Components - DÍA 5 Sub-paso 5.2.1
import AnalyticsComponents from '@/components/analytics'

// 👮 Supervisor Components - DÍA 5 Sub-paso 5.3.1
import SupervisorComponents from '@/components/supervisor'

// 🛣️ Route Components - DÍA 5 Sub-paso 5.4.1
import RouteAgenciesList from '@/components/route/RouteAgenciesList.vue'
import IncidentReportDialog from '@/components/route/IncidentReportDialog.vue'

// Componentes field-supervisor - DÍA 5 PASO 5.4
import {
  FieldSupervisorDashboard,
  FieldOperationsPanel,
  FieldTeamsPanel,
  FieldResourcesPanel,
  FieldCommunicationsPanel,
  FieldPerformanceDashboard
} from '@/components/field-supervisor'

// 🚀 Performance Optimization - DÍA 4 PASO 4.3
import { PerformancePlugin } from '@/plugins/performance'

// 🔌 Realtime WebSocket Plugin - DÍA 1 PASO 1.3
import realtimePlugin from '@/plugins/realtime'

// Toast Notifications - TEMPORALMENTE DESHABILITADO
// import Toast from 'vue-toastification'
// import 'vue-toastification/dist/index.css'
// import '@/assets/styles/toast.css'
// import '@/assets/styles/animations.css'

// Vuetify configuration
const vuetify = createVuetify({
  components,
  directives,
  icons: {
    // Set SVG MDI envuelto: convierte 'mdi-*' en path o 'mdi:<name>' incluso en props prepend/append
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },
  blueprint: md3,
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        colors: {
          primary: '#667eea',
          secondary: '#764ba2',
          accent: '#f093fb',
          success: '#4facfe',
          warning: '#43e97b',
          error: '#f5576c',
          surface: '#fafbfc',
          background: '#f8fafc'
        }
      },
      dark: {
        colors: {
          primary: '#667eea',
          secondary: '#764ba2',
          accent: '#f093fb',
          success: '#4facfe',
          warning: '#43e97b',
          error: '#f5576c'
        }
      }
    }
  }
})

// Toast configuration - versión corregida
const toastOptions = {
  position: "top-right" as const,
  timeout: 4000,
  closeOnClick: true,
  hideProgressBar: false,
  icon: true
}

const pinia = createPinia()

// Marca global temprana de runtime solo en desarrollo
if (import.meta.env.DEV) {
  ;(function(){
    ;(window as any).__VUE_RUNTIME_EVAL__ = {
      started: Date.now(),
      clicks: 0
    }
    window.addEventListener('click',()=>{
      try { (window as any).__VUE_RUNTIME_EVAL__.clicks++ } catch {}
    }, true)
    console.log('[RUNTIME EVAL] Runtime hook activo (DEV only)')
    
    // 🔧 Hook específico para invalidar caché de Groups Management
    ;(window as any).__GROUPS_MGMT_CACHE_BUST__ = Date.now()
    console.log('[GROUPS CACHE] Cache invalidation hook aplicado')
  })()
}

const app = createApp(App)

app.use(pinia)
app.use(router)
app.use(vuetify)
// Compatibilidad: manejar <v-icon> con texto en slot (mdi-*) mapeándolo a prop icon
import IconCompat from '@/plugins/iconCompat'
app.use(IconCompat)

// 🔌 Realtime WebSocket Integration - DÍA 1 PASO 1.3

// 📝 Optimización de Formularios Inteligentes - Módulo 3.2.4
// Usar el plugin con configuración por defecto (evita errores de tipos)
app.use(FormOptimizationPlugin)

// 🚀 Performance Optimization System - DÍA 4 PASO 4.3
app.use(PerformancePlugin, {
  defaultTTL: 5 * 60 * 1000, // 5 minutos
  maxCacheSize: 100,
  debounceTime: 300,
  preloadEnabled: true,
  monitoringEnabled: true
})

// Registrar componentes de formularios optimizados
app.use(FormComponents)

// 📊 Registrar componentes de Analytics - DÍA 5 Sub-paso 5.2.1
app.use(AnalyticsComponents)

// 👮 Registrar componentes de Supervisor - DÍA 5 Sub-paso 5.3.1 + PASO 5.4
app.use(SupervisorComponents)

// 🛣️ Registrar componentes de Route - DÍA 5 Sub-paso 5.4.1
app.component('RouteAgenciesList', RouteAgenciesList)
app.component('IncidentReportDialog', IncidentReportDialog)

// Registrar componentes field-supervisor globalmente - DÍA 5 PASO 5.4
app.component('FieldSupervisorDashboard', FieldSupervisorDashboard)
app.component('FieldOperationsPanel', FieldOperationsPanel)
app.component('FieldTeamsPanel', FieldTeamsPanel)
app.component('FieldResourcesPanel', FieldResourcesPanel)
app.component('FieldCommunicationsPanel', FieldCommunicationsPanel)
app.component('FieldPerformanceDashboard', FieldPerformanceDashboard)

// app.use(Toast, toastOptions) // TEMPORALMENTE DESHABILITADO

// 🔐 Inicializar auth store para restaurar sesión
import { useAuthStore } from '@/stores/auth'
const authStore = useAuthStore()
// Inicializar auth primero para obtener user/rol reales
authStore.initialize()
// Conectar realtime SOLO después de auth para evitar userId provisional
app.use(realtimePlugin)

app.mount('#app')

// 🧪 Test de conectividad al montar la aplicación (Temporalmente deshabilitado)
// import ApiTestSuite from '@/services/api-test'

// // Ejecutar test básico cuando la aplicación esté lista
// setTimeout(async () => {
//   console.log('🚀 MGI Frontend initialized, testing backend connectivity...')
//   await ApiTestSuite.testBasicConnectivity()
// }, 1000)

console.log('🚀 MGI Frontend initialized successfully!')